import { useCallback, useMemo } from 'react';

import {
  InfiniteData,
  QueryClient,
  keepPreviousData,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPost, callPut } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  CampaignSubscriber,
  CampaignSubscribers,
  NewCampaignSubscriber,
} from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface BulkUpsertProps {
  campaign_id: string;
  subscribers: NewCampaignSubscriber[];
  replace?: boolean;
}

interface BulkUpsertResponse {
  created: number;
  failed: number;
  updated: number;
  errors?: Array<{ index: number; field?: string; message: string; value?: string }>;
}

export interface CampaignSubscriberParams {
  q?: string;
  status?: string;
  error_type?: string;
  channels?: string[];
  sort?: string;
  limit?: number;
}

export const API = Object.freeze({
  listCampaignSubscribers: async (
    campaignId: string,
    cursor?: string,
    params?: CampaignSubscriberParams
  ): Promise<CampaignSubscribers> =>
    callGet(endpoints.campaignSubscribers(campaignId, { cursor, ...params })),

  bulkUpsertSubscribers: async ({
    campaign_id,
    subscribers,
    replace,
  }: BulkUpsertProps): Promise<BulkUpsertResponse> =>
    callPut(endpoints.campaignSubscribersUpsert(campaign_id, replace), { subscribers }),

  stopCampaignSubscriber: async (campaignId: string, subscriberId: string) =>
    callPost(endpoints.stopCampaignSubscriber(campaignId, subscriberId), {}),

  resumeCampaignSubscriber: async (campaignId: string, subscriberId: string) =>
    callPost(endpoints.resumeCampaignSubscriber(campaignId, subscriberId), {}),

  reevaluateCampaignSubscriber: async (campaignId: string, subscriberId: string) =>
    callPost(endpoints.reevaluateCampaignSubscriber(campaignId, subscriberId), {}),
});

export const onSubscribersUpdated = (
  queryClient: QueryClient,
  campaignId: string,
  campaign_subscribers: CampaignSubscriber[]
) => {
  const queryKey = [endpoints.campaignSubscribers(campaignId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<InfiniteData<CampaignSubscribers>>(queryKey, (prev) => {
      if (!prev) {
        return prev;
      }
      const newPages = prev.pages.map((page, index) => {
        if (index === 0) {
          return { ...page, subscribers: campaign_subscribers };
        }
        return page;
      });
      return { ...prev, pages: newPages };
    });
  }
};

export const onSubscribersAdded = (
  queryClient: QueryClient,
  campaignId: string,
  newSubscribers: CampaignSubscriber[]
) => {
  const queryKey = [endpoints.campaignSubscribers(campaignId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<InfiniteData<CampaignSubscribers>>(queryKey, (prev) => {
      if (!prev) {
        return prev;
      }
      const newPages = prev.pages.map((page, index) => {
        if (index === 0) {
          return { ...page, subscribers: [...newSubscribers, ...page.subscribers] };
        }
        return page;
      });
      return { ...prev, pages: newPages };
    });
  }
};

export const onSubscribersRemoved = (
  queryClient: QueryClient,
  campaignId: string,
  subscriberIds: string[]
) => {
  const queryKey = [endpoints.campaignSubscribers(campaignId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<InfiniteData<CampaignSubscribers>>(queryKey, (prev) => {
      if (!prev) {
        return prev;
      }
      const newPages = prev.pages.map((page) => ({
        ...page,
        subscribers: page.subscribers.filter(
          (subscriber) => !subscriberIds.includes(subscriber.subscriber_id)
        ),
      }));
      return { ...prev, pages: newPages };
    });
  }
};

export const invalidateCampaignSubscribers = (queryClient: QueryClient, campaignId: string) => {
  queryClient.invalidateQueries({ queryKey: [endpoints.campaignSubscribers(campaignId)] });
};

export const fetchAllCampaignSubscribers = async (
  campaignId: string,
  params?: CampaignSubscriberParams
): Promise<CampaignSubscriber[]> => {
  const all: CampaignSubscriber[] = [];
  let cursor: string | undefined;
  do {
    const page = await API.listCampaignSubscribers(campaignId, cursor, params);
    all.push(...page.subscribers);
    cursor = page.pagination?.has_more ? page.pagination.next_cursor : undefined;
  } while (cursor);
  return all;
};

export const useCampaignSubscribers = (
  campaignId?: string,
  params?: CampaignSubscriberParams,
  refetchInterval?: number | false
) => {
  const queryClient = useQueryClient();

  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );

  const {
    data: subscribersData,
    status: listStatus,
    isLoading: isSubscribersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchSubscribers,
  } = useInfiniteQuery({
    queryKey: [
      endpoints.campaignSubscribers(campaignId),
      params?.q,
      params?.status,
      params?.error_type,
      params?.channels,
      params?.sort,
    ],
    queryFn: async ({ pageParam }) => {
      return API.listCampaignSubscribers(campaignId, pageParam?.cursor, params);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_more && lastPage.pagination?.next_cursor) {
        return { cursor: lastPage.pagination.next_cursor };
      }
      return undefined;
    },
    initialPageParam: {} as { cursor?: string },
    enabled: !!campaignId && canRead,
    staleTime: 0, // Set to 0 so invalidation always triggers refetch
    refetchInterval: refetchInterval ?? false, // Use provided interval or disable by default
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: bulkUpsertSubscribersAsync } = useMutation<
    BulkUpsertResponse,
    Error,
    BulkUpsertProps
  >({
    // Treat "every subscriber failed" as a mutation error so the caller's
    // catch returns null and Continue stays blocked. Partial success
    // (some saved + some failed) resolves normally; `onSuccess` surfaces
    // a warning toast so the user knows rows were dropped.
    mutationFn: async (payload) => {
      const res = await API.bulkUpsertSubscribers(payload);
      const saved = res.created + res.updated;
      if (saved === 0 && res.failed > 0) {
        const first = res.errors?.[0]?.message ?? i18next.t('campaigns.audience_save_all_failed');
        throw new Error(first);
      }
      return res;
    },
    onSuccess: (data, variables) => {
      // Use campaign_id from variables, not from closure (which might be undefined)
      const mutationCampaignId = variables.campaign_id;

      // Invalidate subscribers query to refetch the list (matches all filter/status variants)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === endpoints.campaignSubscribers(mutationCampaignId);
        },
      });

      // Invalidate campaign queries to update subscriber counts in the campaign object
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            (key[0] === endpoints.campaigns || key[0] === endpoints.campaign(mutationCampaignId))
          );
        },
      });

      // Partial success: warn the user. (Fully-failed case threw above.)
      if (data.failed > 0) {
        toast.warning(
          i18next.t('campaigns.audience_save_partial', {
            0: data.created + data.updated,
            1: data.failed,
          })
        );
      }
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: stopSubscriberAsync, status: stopSubscriberStatus } = useMutation({
    mutationFn: ({ campaignId, subscriberId }: { campaignId: string; subscriberId: string }) =>
      API.stopCampaignSubscriber(campaignId, subscriberId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) && key[0] === endpoints.campaignSubscribers(variables.campaignId)
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: [endpoints.campaignSubscriber(variables.campaignId, variables.subscriberId)],
      });
    },
    meta: { errorToast: true },
  });

  // Flatten all subscribers from all pages
  const allSubscribers = useMemo(() => {
    return subscribersData?.pages.flatMap((page) => page.subscribers) || [];
  }, [subscribersData]);

  const subscribersByStatus = useCallback(
    (statusFilter: string) => {
      return allSubscribers.filter((s) => {
        // Determine status based on timestamps
        if (s.last_failed_at) {
          return statusFilter === 'failed';
        }
        if (s.last_replied_at) {
          return statusFilter === 'replied';
        }
        if (s.last_read_at) {
          return statusFilter === 'read';
        }
        if (s.last_delivered_at) {
          return statusFilter === 'delivered';
        }
        if (s.last_sent_at) {
          return statusFilter === 'sent';
        }
        return statusFilter === 'pending';
      });
    },
    [allSubscribers]
  );

  const subscriberEmails = useMemo(
    () => allSubscribers.map((s) => s.email).filter(Boolean),
    [allSubscribers]
  );

  const subscriberPhones = useMemo(
    () => allSubscribers.map((s) => s.phone).filter(Boolean),
    [allSubscribers]
  );

  return {
    subscribersData, // Return the pages structure for useServerPagination
    campaign_subscribers: allSubscribers, // Flattened list for backward compatibility
    listStatus,
    isSubscribersLoading,
    subscriberEmails,
    subscriberPhones,
    bulkUpsertSubscribersAsync,
    stopSubscriberAsync,
    stopSubscriberStatus,
    subscribersByStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetchSubscribers,
    totalRows: subscribersData?.pages[0]?.pagination?.total,
  };
};
